<template>
  <div v-if="!collapsed" class="bracket-minimap" :class="{ collapsed }">
    <div class="minimap-header">
      <span class="minimap-title">Map</span>
      <v-tooltip text="Close minimap" location="bottom">
        <template #activator="{ props: tip }">
          <v-btn
            v-bind="tip"
            icon="mdi-close"
            size="x-small"
            variant="text"
            density="compact"
            @click="$emit('toggle-collapsed')"
          />
        </template>
      </v-tooltip>
    </div>
    <div class="minimap-content" ref="minimapContainerRef">
      <svg
        :width="minimapWidth"
        :height="minimapHeight"
        class="minimap-svg"
        @mousedown="handleMinimapMouseDown"
      >
        <!-- Bracket items -->
        <rect
          v-for="(item, index) in scaledItems"
          :key="`item-${index}`"
          :x="item.x"
          :y="item.y"
          :width="item.width"
          :height="item.height"
          :fill="item.color"
          class="minimap-item"
        />

        <!-- Viewport indicator -->
        <rect
          :x="viewport.x"
          :y="viewport.y"
          :width="viewport.width"
          :height="viewport.height"
          fill="rgba(var(--v-theme-primary), 0.08)"
          stroke="rgba(var(--v-theme-primary), 0.7)"
          stroke-width="2"
          class="minimap-viewport"
          style="cursor: move"
        />
      </svg>
    </div>
  </div>
  <div v-else class="bracket-minimap collapsed">
    <v-tooltip text="Open minimap" location="bottom">
      <template #activator="{ props: tip }">
        <v-btn
          v-bind="tip"
          icon="mdi-map-outline"
          size="small"
          variant="tonal"
          @click="$emit('toggle-collapsed')"
        />
      </template>
    </v-tooltip>
  </div>
</template>

<script setup>
const props = defineProps({
  collapsed: {
    type: Boolean,
    required: true
  },
  bracketWidth: {
    type: Number,
    required: true
  },
  bracketHeight: {
    type: Number,
    required: true
  },
  items: {
    type: Array,
    required: true
  },
  containerWidth: {
    type: Number,
    required: true
  },
  containerHeight: {
    type: Number,
    required: true
  },
  panZoomState: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['toggle-collapsed', 'navigate-to'])

const minimapContainerRef = ref(null)
const isDragging = ref(false)

// Minimap dimensions
const minimapWidth = 240
const minimapHeight = 180

// Calculate scale factor to fit bracket in minimap
const scaleFactorX = computed(() => minimapWidth / props.bracketWidth)
const scaleFactorY = computed(() => minimapHeight / props.bracketHeight)
const scaleFactor = computed(() => Math.min(scaleFactorX.value, scaleFactorY.value))

// Scale bracket items for minimap
const scaledItems = computed(() => {
  return props.items.map(item => ({
    x: item.x * scaleFactor.value,
    y: item.y * scaleFactor.value,
    width: item.width * scaleFactor.value,
    height: item.height * scaleFactor.value,
    color: 'rgb(var(--v-theme-borderLight))',
  }))
})

// Calculate viewport rectangle based on pan/zoom state
const viewport = computed(() => {
  const { x, y, scale } = props.panZoomState

  // Calculate visible area in bracket coordinates
  const visibleWidth = props.containerWidth / scale
  const visibleHeight = props.containerHeight / scale
  const visibleX = -x / scale
  const visibleY = -y / scale

  // Scale to minimap coordinates
  return {
    x: Math.max(0, visibleX * scaleFactor.value),
    y: Math.max(0, visibleY * scaleFactor.value),
    width: Math.min(minimapWidth, visibleWidth * scaleFactor.value),
    height: Math.min(minimapHeight, visibleHeight * scaleFactor.value),
  }
})

const handleMinimapMouseDown = (event) => {
  if (event.target instanceof SVGRectElement &&
      event.target.classList.contains('minimap-viewport')) {
    isDragging.value = true
    handleMinimapDrag(event)
  } else {
    // Click on minimap to center viewport
    const rect = event.currentTarget.getBoundingClientRect()
    const clickX = event.clientX - rect.left
    const clickY = event.clientY - rect.top

    // Convert minimap coordinates to bracket coordinates
    const bracketX = (clickX / scaleFactor.value) - (props.containerWidth / props.panZoomState.scale / 2)
    const bracketY = (clickY / scaleFactor.value) - (props.containerHeight / props.panZoomState.scale / 2)

    // Emit navigate event (will be converted to pan coordinates in parent)
    emit('navigate-to', -bracketX * props.panZoomState.scale, -bracketY * props.panZoomState.scale)
  }
}

const handleMinimapDrag = (event) => {
  if (!isDragging.value) return

  const rect = minimapContainerRef.value?.querySelector('svg')?.getBoundingClientRect()
  if (!rect) return

  const clickX = event.clientX - rect.left
  const clickY = event.clientY - rect.top

  // Convert to bracket coordinates (center the viewport on click point)
  const bracketX = (clickX / scaleFactor.value) - (props.containerWidth / props.panZoomState.scale / 2)
  const bracketY = (clickY / scaleFactor.value) - (props.containerHeight / props.panZoomState.scale / 2)

  emit('navigate-to', -bracketX * props.panZoomState.scale, -bracketY * props.panZoomState.scale)
}

const handleMouseMove = (event) => {
  if (isDragging.value) {
    handleMinimapDrag(event)
  }
}

const handleMouseUp = () => {
  isDragging.value = false
}

onMounted(() => {
  document.addEventListener('mousemove', handleMouseMove)
  document.addEventListener('mouseup', handleMouseUp)
})

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleMouseMove)
  document.removeEventListener('mouseup', handleMouseUp)
})
</script>

<style scoped>
.bracket-minimap {
  position: absolute;
  bottom: 16px;
  right: 16px;
  z-index: 1001;
  background: rgb(var(--v-theme-surface));
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 14px;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s ease;
}

.bracket-minimap.collapsed {
  width: auto;
  height: auto;
  background: transparent;
  box-shadow: none;
}

.minimap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: rgb(var(--v-theme-light));
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));
}

.minimap-title {
  font-size: 10.5px;
  font-weight: 700;
  color: rgb(var(--v-theme-textSubtle));
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.minimap-content {
  padding: 8px;
  background: rgb(var(--v-theme-light));
}

.minimap-svg {
  display: block;
  border: 1px solid rgb(var(--v-theme-borderLight));
  background: rgb(var(--v-theme-surface));
  border-radius: 7px;
}

.minimap-item {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.minimap-item:hover {
  opacity: 1;
}

.minimap-viewport {
  cursor: move;
  pointer-events: all;
}
</style>
